import { Globe, RefreshCw } from 'lucide-react';

import { NoInformation as NoInformationIcon } from '@/components/chariot-ui';
import { Entitled } from '@/components/Entitled';
import { NoData } from '@/components/legacy-ui/NoData';
import { MarkdownPreview } from '@/components/markdown/MarkdownPreview';
import { Button } from '@/components/ui/button';
import { useRefreshSeedData } from '@/sections/seeds/hooks/actions/useRefreshSeedData';
import type { WhoisHistoryStatus } from '@/sections/seeds/hooks/drawer/useSeedDrawerData';
import { Seed as SeedDiscriminated } from '@/sections/seeds/types';
import { Seed } from '@/types';
import { cn } from '@/utils/classname';

interface DomainHistoryTabProps {
  seed: Seed;
  history?: string[];
  whoisHistoryStatus?: WhoisHistoryStatus;
  isDomainUnregistered?: boolean;
}

export function DomainHistoryTab({
  seed,
  history,
  whoisHistoryStatus,
  isDomainUnregistered,
}: DomainHistoryTabProps) {
  const { refresh, isRefreshing } = useRefreshSeedData();
  const onRefresh = () => refresh([seed as unknown as SeedDiscriminated]);

  const refreshButton = (
    <Entitled entitlement="write_jobs" behavior="disable">
      <Button onClick={onRefresh} disabled={isRefreshing} size="sm">
        <RefreshCw
          className={cn('size-4', isRefreshing && 'motion-safe:animate-spin')}
        />
        Refresh
      </Button>
    </Entitled>
  );

  // Demoted (ghost) variant — used only by the unregistered state, where refresh
  // is an honest "re-check status" affordance, not a false remedy.
  const ghostRefreshButton = (
    <Entitled entitlement="write_jobs" behavior="disable">
      <Button
        onClick={onRefresh}
        disabled={isRefreshing}
        size="sm"
        variant="ghost"
      >
        <RefreshCw
          className={cn('size-4', isRefreshing && 'motion-safe:animate-spin')}
        />
        Refresh
      </Button>
    </Entitled>
  );

  // Only the details envelope (isDomainUnregistered) proves current
  // unregistration — a preserved history marker can be stale after
  // re-registration. Stored history of a dropped domain must still render,
  // so this state only shows when no history records exist. Placed first so
  // it can't be shadowed.
  if (isDomainUnregistered && (!history || history.length === 0)) {
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={
            <Globe className="text-chariot-stroke mb-4 size-12 @xl:size-16 @2xl:size-20 @3xl:size-[100px]" />
          }
          title="This domain is not registered"
          description="Its registry reports no active registration, and no past WHOIS history has been collected. If this domain was previously registered, its earlier history would appear here."
          action={ghostRefreshButton}
        />
      </div>
    );
  }

  if (whoisHistoryStatus === 'empty') {
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={
            <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
          }
          title="No WHOIS history available for this TLD"
          action={refreshButton}
        />
      </div>
    );
  }

  if (whoisHistoryStatus === 'error') {
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={
            <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
          }
          title="WHOIS history unavailable"
          description="The WHOIS history provider returned an error (often temporary). Refresh to retry."
          action={refreshButton}
        />
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <NoData
          icon={
            <NoInformationIcon className="text-chariot-stroke mb-4 text-[100px]" />
          }
          title="No WHOIS history collected yet"
          action={refreshButton}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 [&_code]:p-0!">
      {history.map((data: string, i: number) => (
        <MarkdownPreview
          key={i}
          source={`\`\`\`json\n${JSON.stringify(data, null, 2)}\n\`\`\``}
        />
      ))}
    </div>
  );
}
